import { inject, Injectable, NgZone } from '@angular/core';
import { AuthService } from '../../auth.service';
import { Router } from '@angular/router';
import { AUTH_ROUTES, DASHBOARD_ROUTES } from '../../../../shared/constants/routes.constants';
import { StatusModalService } from '../../../../shared/services/status-modal/status-modal.service';
import { distinctUntilChanged, skip } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private readonly idleTimeoutMs = 60 * 60 * 1000; // 1 hour
  // private readonly idleTimeoutMs = 60 * 1000; // 1 minute
  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  private timeoutId: number | null = null;
  private resetTimerFn: (() => void) | null = null;
  private visibilityChangeFn: (() => void) | null = null;

  private readonly _ngZone = inject(NgZone);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _statusModalService = inject(StatusModalService);

  private get _isSignInPage() {
    return this._router.url === AUTH_ROUTES.FULL.SIGN_IN;
  }

  public start(): void {
    this._ngZone.runOutsideAngular(() => {
      this._checkIfAuthenticated();
    });
  }

  public stop(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.resetTimerFn) {
      this.activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.resetTimerFn!);
      });
      this.resetTimerFn = null;
    }
  }

  private async _onIdle(): Promise<void> {
    void this._statusModalService.info({
      title: 'Session Expired',
      animatedIcon: 'info',
      message: "You've been idle for too long.",
      description: 'Please sign in again to continue.',
    });

    await this._signOut();
  }

  private _initializeIdleChecker(): void {
    if (this.resetTimerFn) {
      this.activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, this.resetTimerFn!);
      });
      this.resetTimerFn = null;
    }

    this.resetTimerFn = () => {
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId);
      }

      this.timeoutId = window.setTimeout(() => {
        this._ngZone.run(() => this._onIdle());
      }, this.idleTimeoutMs);
    };

    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.resetTimerFn!);
    });

    this.resetTimerFn();
  }

  private _checkIfAuthenticated(): void {
    this._authService.isAuthenticated$
      .pipe(distinctUntilChanged(), skip(1))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this._listenForTabActivity();
          this._initializeIdleChecker();
          this._navigateToHomePage();
        } else if (document.hidden) {
          if (this._isSignInPage) return;
          void this._statusModalService.info({
            title: 'Session Expired',
            animatedIcon: 'info',
            message: 'Please sign in again to continue.',
          });
          this._signOut();
        }
      });
  }

  private _listenForTabActivity(): void {
    if (this.visibilityChangeFn) {
      document.removeEventListener('visibilitychange', this.visibilityChangeFn);
      this.visibilityChangeFn = null;
    }

    this.visibilityChangeFn = () => {
      if (!document.hidden) {
        this.stop();
      } else {
        this._initializeIdleChecker();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeFn);
  }

  private _navigateToHomePage(): void {
    if (!this._isSignInPage) return;
    if (!document.hidden) return;

    this._ngZone.run(() => {
      this._router.navigate(DASHBOARD_ROUTES.ROUTER_LINK.DASHBOARD);
    });
  }

  private async _signOut(): Promise<void> {
    if (this._authService.isSignedIn) {
      await this._authService.signOut();
    }
    this.stop();
    this._ngZone.run(() => {
      setTimeout(() => {
        this._router.navigate(AUTH_ROUTES.ROUTER_LINK.SIGN_IN);
      }, 500);
    });
  }
}
